import { useMutateUpdateClinicalTask } from '@/hooks/mutation/useMutateUpdateClinicalTask';
import { PendingObjectRow, TaskObjectRow } from '@heidi/agents';
import { untitledTitle, type WrittenObject } from '@heidi/agents/data';
import { DataTableEnumCell, type EnumCellOption } from '@heidi/design-system/data-table';
import { StatusIndicator } from '@heidi/design-system/status-indicator';
import { ClinicalTaskStatus, type AssigneeType } from '@heidi/schema/ml-scribe';
import { ClinicalTaskAssigneeSelector, clinicalTaskStatusChipColor } from '@heidi/tasks';
import {
  useMutateAssignClinicalTask,
  useMutateUnassignClinicalTask,
  useQueryClinicalTaskById,
} from '@heidi/tasks/data';
import Router from 'next/router';
import { useState } from 'react';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';

const messages = defineMessages({
  assignTask: { defaultMessage: 'Assign task', id: 'hlujwJOcTn' },
  assignedTo: { defaultMessage: 'Assigned to {name}', id: 'ATvbzLeJNh' },
  assigned: { defaultMessage: 'Assigned', id: 'iZDRGORYVZ' },
  toDo: { defaultMessage: 'To do', id: 'vwqEeHz1zx' },
  done: { defaultMessage: 'Done', id: 'JXdbo8Vnlw' },
  archived: { defaultMessage: 'Archived', id: '0HT+IbyW6O' },
});

const statusOptions = (intl: IntlShape): EnumCellOption<ClinicalTaskStatus>[] => [
  {
    value: ClinicalTaskStatus.todo,
    label: intl.formatMessage(messages.toDo),
    leadingIcon: <StatusIndicator status="in-progress" size="xs" />,
    color: clinicalTaskStatusChipColor[ClinicalTaskStatus.todo],
  },
  {
    value: ClinicalTaskStatus.done,
    label: intl.formatMessage(messages.done),
    leadingIcon: <StatusIndicator status="done" size="xs" />,
    color: clinicalTaskStatusChipColor[ClinicalTaskStatus.done],
  },
  {
    value: ClinicalTaskStatus.archived,
    label: intl.formatMessage(messages.archived),
    leadingIcon: <StatusIndicator status="archive" size="xs" />,
    color: clinicalTaskStatusChipColor[ClinicalTaskStatus.archived],
  },
];

type AssigneeSelection = {
  id: string | null;
  name: string | null;
  avatarUrl: string | null;
  type?: AssigneeType;
};

export const TaskRow = ({ object }: { object: WrittenObject }) => {
  const intl = useIntl();
  const task = useQueryClinicalTaskById(object.id);
  const { mutate: updateTask } = useMutateUpdateClinicalTask();
  const { mutate: assignTask } = useMutateAssignClinicalTask();
  const { mutate: unassignTask } = useMutateUnassignClinicalTask();
  const [pendingStatus, setPendingStatus] = useState<ClinicalTaskStatus>();
  const [pendingAssignee, setPendingAssignee] = useState<AssigneeSelection>();
  const data = task.data;
  const fallbackTitle = object.fallbackTitle ?? untitledTitle('task', intl);
  if (!data) return <PendingObjectRow title={fallbackTitle} />;

  // Task mutations patch list caches only; refetch the by-id read before dropping the override.
  const settle = (clear: () => void) => ({
    onSettled: () => void task.refetch().finally(clear),
  });
  const assignee: AssigneeSelection = pendingAssignee ?? {
    id: data.assignee?.id ?? data.assignee_id ?? null,
    name: data.assignee_name ?? null,
    avatarUrl: data.assignee_avatar_url ?? null,
    type: data.assignee?.type,
  };

  const changeStatus = (status: ClinicalTaskStatus) => {
    setPendingStatus(status);
    updateTask(
      { taskId: data.id, taskData: { status } },
      settle(() => setPendingStatus(undefined))
    );
  };

  const changeAssignee = (
    id: string | null,
    name: string | null,
    avatarUrl: string | null,
    type?: AssigneeType
  ) => {
    setPendingAssignee({ id, name, avatarUrl, type });
    const options = settle(() => setPendingAssignee(undefined));
    if (id) {
      assignTask(
        {
          taskId: data.id,
          assigneeId: id,
          assigneeName: name,
          assigneeAvatarUrl: avatarUrl,
          assigneeType: type,
        },
        options
      );
    } else {
      unassignTask(data.id, options);
    }
  };

  return (
    <TaskObjectRow
      title={data.title ?? fallbackTitle}
      patientName={data.patient_name ?? undefined}
      onOpen={() => void Router.push(`/tasks/${data.id}`)}
      statusSlot={
        <DataTableEnumCell<ClinicalTaskStatus>
          value={pendingStatus ?? data.status}
          options={statusOptions(intl)}
          onChange={changeStatus}
          hideLabel
        />
      }
      assigneeSlot={
        <ClinicalTaskAssigneeSelector
          assigneeId={assignee.id}
          assigneeName={assignee.name}
          assigneeAvatarUrl={assignee.avatarUrl}
          assigneeType={assignee.type}
          enableTeams={false}
          compact
          align="end"
          aria-label={
            assignee.id
              ? intl.formatMessage(messages.assignedTo, {
                  name: assignee.name ?? intl.formatMessage(messages.assigned),
                })
              : intl.formatMessage(messages.assignTask)
          }
          onValueChange={changeAssignee}
        />
      }
    />
  );
};
